import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import VoiceInput from "@/components/VoiceInput";
import { Plus, Trash2, ShieldAlert } from "lucide-react";

/* Reusable stat/textarea rows hoisted to module scope — placed INSIDE parent
 * render body previously, which caused React to remount the input on every
 * keystroke (focus loss). */
function VitalField({ k, label, unit, f, setF }) {
  return (
    <div>
      <Label className="text-[10px]">{label} <span className="text-slate-400">{unit}</span></Label>
      <Input type="number" step="0.1" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-8 text-sm" data-testid={`nabh-vital-${k}`}/>
    </div>
  );
}

function VoiceField({ k, label, rows = 2, testidPrefix, f, setF }) {
  return (
    <div>
      <Label className="text-[10px]">{label}</Label>
      <VoiceInput rows={rows} testid={`${testidPrefix}-${k}`} value={f[k] || ""} onChange={(v) => setF({ ...f, [k]: v })}/>
    </div>
  );
}

/* Combined NABH clinical bundle:
 *   §3 Assessment · §4 Vitals · §5 Progress Notes · §8 Allergies
 * All actions are append-only. Latest snapshots are shown at top; full
 * history is a scrollable list below. */
export default function IPDClinicalBundle({ ipd, onChange }) {
  const iid = ipd.id;
  const [assessments, setAssessments] = useState([]);
  const [vitals, setVitals] = useState([]);
  const [progress, setProgress] = useState([]);
  const [allergies, setAllergies] = useState({ latest: ipd.allergies || null, history: [] });

  const load = () => Promise.all([
    api.get(`/ipd/${iid}/assessments`).then((r) => setAssessments(r.data)).catch(() => {}),
    api.get(`/ipd/${iid}/vitals`).then((r) => setVitals(r.data)).catch(() => {}),
    api.get(`/ipd/${iid}/progress-notes`).then((r) => setProgress(r.data)).catch(() => {}),
    api.get(`/ipd/${iid}/allergies`).then((r) => setAllergies(r.data)).catch(() => {}),
  ]).then(() => onChange && onChange());
  useEffect(() => { if (iid) load(); /* eslint-disable-next-line */ }, [iid]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <AllergyCard iid={iid} allergies={allergies} onSaved={load}/>
      <VitalsCard iid={iid} vitals={vitals} onSaved={load}/>
      <AssessmentCard iid={iid} assessments={assessments} onSaved={load}/>
      <ProgressCard iid={iid} progress={progress} onSaved={load}/>
    </div>
  );
}

/* ---------- §8 Allergy ---------- */
function AllergyCard({ iid, allergies, onSaved }) {
  const [nka, setNka] = useState(!!allergies?.latest?.no_known_allergy);
  const [entries, setEntries] = useState(allergies?.latest?.entries || []);
  const [remarks, setRemarks] = useState(allergies?.latest?.remarks || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setNka(!!allergies?.latest?.no_known_allergy);
    setEntries(allergies?.latest?.entries || []);
    setRemarks(allergies?.latest?.remarks || "");
  }, [allergies]);

  const addEntry = () => setEntries([...entries, { substance: "", reaction: "", severity: "Moderate" }]);
  const updEntry = (i, k, v) => setEntries(entries.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const rmEntry = (i) => setEntries(entries.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!nka && entries.filter((e) => (e.substance || "").trim()).length === 0) {
      return toast.error("Add at least one allergy OR tick 'No Known Allergy'");
    }
    setBusy(true);
    try {
      await api.post(`/ipd/${iid}/allergies`, { no_known_allergy: nka, entries: nka ? [] : entries.filter((e) => e.substance), remarks });
      toast.success("Allergy status recorded");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border p-4" data-testid="nabh-allergy-card">
      <div className="flex items-center justify-between mb-2">
        <div className="label-tiny flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5"/> §8 Allergies</div>
        {allergies?.latest && <Badge className="bg-slate-100 text-slate-700 rounded-sm text-[10px]">Last: {new Date(allergies.latest.created_at).toLocaleString("en-IN")} · {allergies.latest.created_by_name}</Badge>}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={nka} onChange={(e) => { setNka(e.target.checked); if (e.target.checked) setEntries([]); }} data-testid="nabh-nka"/>
        No Known Allergy (must be explicitly confirmed)
      </label>
      {!nka && (
        <div className="mt-2 space-y-2">
          {entries.map((e, i) => (
            <div key={i} className="grid grid-cols-[1fr,1fr,120px,32px] gap-2">
              <Input placeholder="Substance (drug, food, latex…)" value={e.substance} onChange={(ev) => updEntry(i, "substance", ev.target.value)} data-testid={`nabh-allergy-sub-${i}`}/>
              <Input placeholder="Reaction (rash, anaphylaxis…)" value={e.reaction} onChange={(ev) => updEntry(i, "reaction", ev.target.value)}/>
              <select value={e.severity} onChange={(ev) => updEntry(i, "severity", ev.target.value)} className="border h-9 text-xs px-2">
                <option>Mild</option><option>Moderate</option><option>Severe</option>
              </select>
              <button onClick={() => rmEntry(i)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
          <Button variant="outline" onClick={addEntry} size="sm" className="text-xs"><Plus className="w-3 h-3 mr-1"/> Add allergy</Button>
        </div>
      )}
      <div className="mt-2">
        <Label className="text-xs">Remarks</Label>
        <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} className="h-8"/>
      </div>
      <Button onClick={save} disabled={busy} className="mt-3 bg-red-700 hover:bg-red-800 rounded-sm" data-testid="nabh-allergy-save">Save Allergy Status</Button>
    </div>
  );
}

/* ---------- §4 Vitals ---------- */
function VitalsCard({ iid, vitals, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const payload = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" || v === null ? null : (["temperature", "weight"].includes(k) ? parseFloat(v) : parseInt(v))]));
    if (!Object.values(payload).some((v) => v !== null && !Number.isNaN(v))) return toast.error("Enter at least one value");
    setBusy(true);
    try {
      await api.post(`/ipd/${iid}/vitals`, payload);
      toast.success("Vitals recorded");
      setF({});
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border p-4" data-testid="nabh-vitals-card">
      <div className="label-tiny mb-2">§4 Vitals</div>
      <div className="grid grid-cols-4 gap-2">
        <VitalField k="temperature" label="Temp" unit="°F" f={f} setF={setF}/>
        <VitalField k="pulse" label="Pulse" unit="bpm" f={f} setF={setF}/>
        <VitalField k="bp_systolic" label="BP Sys" unit="mmHg" f={f} setF={setF}/>
        <VitalField k="bp_diastolic" label="BP Dia" unit="mmHg" f={f} setF={setF}/>
        <VitalField k="respiratory_rate" label="RR" unit="/min" f={f} setF={setF}/>
        <VitalField k="spo2" label="SpO₂" unit="%" f={f} setF={setF}/>
        <VitalField k="weight" label="Weight" unit="kg" f={f} setF={setF}/>
        <VitalField k="rbs" label="RBS" unit="mg/dL" f={f} setF={setF}/>
      </div>
      <Button onClick={save} disabled={busy} className="mt-2 bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid="nabh-vital-save">+ Record Vitals</Button>
      <div className="mt-3 max-h-40 overflow-y-auto text-[11px]">
        {vitals.length === 0 && <div className="text-slate-400 italic">No vitals yet</div>}
        {vitals.map((v) => (
          <div key={v.id} className="flex justify-between border-b py-1">
            <span>{new Date(v.recorded_at).toLocaleString("en-IN")} · {v.recorded_by_name}</span>
            <span className="font-mono">
              {v.temperature ? `T ${v.temperature}°F ` : ""}
              {v.pulse ? `P ${v.pulse} ` : ""}
              {v.bp_systolic ? `BP ${v.bp_systolic}/${v.bp_diastolic} ` : ""}
              {v.spo2 ? `SpO₂ ${v.spo2}% ` : ""}
              {v.rbs ? `RBS ${v.rbs}` : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- §3 Assessment ---------- */
function AssessmentCard({ iid, assessments, onSaved }) {
  const latest = assessments[0] || {};
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(latest); /* eslint-disable-next-line */ }, [assessments.length]);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/ipd/${iid}/assessment`, {
        chief_complaint: f.chief_complaint || "", history: f.history || "", past_history: f.past_history || "",
        allergies_text: f.allergies_text || "", medication_history: f.medication_history || "",
        provisional_diagnosis: f.provisional_diagnosis || "", final_diagnosis: f.final_diagnosis || "",
        clinical_findings: f.clinical_findings || "", treatment_plan: f.treatment_plan || "",
      });
      toast.success("Assessment saved");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const testidPrefix = "nabh-asmt";
  return (
    <div className="bg-white border p-4 lg:col-span-2" data-testid="nabh-assessment-card">
      <div className="flex items-center justify-between mb-2">
        <div className="label-tiny">§3 Clinical Assessment</div>
        {latest.created_at && <Badge className="bg-slate-100 text-slate-700 rounded-sm text-[10px]">v{assessments.length} · {new Date(latest.created_at).toLocaleString("en-IN")} · {latest.created_by_name || "-"}</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <VoiceField k="chief_complaint" label="Chief Complaint" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="history" label="History of Present Illness" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="past_history" label="Past History" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="medication_history" label="Medication History" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="allergies_text" label="Known Allergies (free text)" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="clinical_findings" label="Clinical Findings" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="provisional_diagnosis" label="Provisional Diagnosis" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="final_diagnosis" label="Final Diagnosis" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <div className="col-span-2"><VoiceField k="treatment_plan" label="Treatment Plan" rows={3} testidPrefix={testidPrefix} f={f} setF={setF}/></div>
      </div>
      <Button onClick={save} disabled={busy} className="mt-3 bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid="nabh-asmt-save">Save Assessment Version</Button>
    </div>
  );
}

/* ---------- §5 Progress Notes ---------- */
function ProgressCard({ iid, progress, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.clinical_status && !f.plan && !f.advice) return toast.error("Fill at least one field");
    setBusy(true);
    try {
      await api.post(`/ipd/${iid}/progress-notes`, {
        clinical_status: f.clinical_status || "", diagnosis: f.diagnosis || "", treatment: f.treatment || "",
        investigation_review: f.investigation_review || "", plan: f.plan || "", advice: f.advice || "",
      });
      toast.success("Progress note added");
      setF({});
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const testidPrefix = "nabh-prog";
  return (
    <div className="bg-white border p-4 lg:col-span-2" data-testid="nabh-progress-card">
      <div className="label-tiny mb-2">§5 Doctor Progress Notes (append-only)</div>
      <div className="grid grid-cols-2 gap-3">
        <VoiceField k="clinical_status" label="Clinical Status" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="diagnosis" label="Working Diagnosis" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="treatment" label="Today's Treatment" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="investigation_review" label="Investigation Review" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="plan" label="Plan" testidPrefix={testidPrefix} f={f} setF={setF}/>
        <VoiceField k="advice" label="Advice" testidPrefix={testidPrefix} f={f} setF={setF}/>
      </div>
      <Button onClick={save} disabled={busy} className="mt-3 bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid="nabh-prog-save">+ Add Progress Note</Button>
      <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
        {progress.length === 0 && <div className="text-slate-400 italic text-[11px]">No progress notes yet</div>}
        {progress.map((p) => (
          <div key={p.id} className="border-l-4 border-blue-300 pl-3 py-2 text-[11px]">
            <div className="flex justify-between text-slate-500">
              <span>{new Date(p.created_at).toLocaleString("en-IN")} · <b>{p.doctor_name}</b></span>
            </div>
            {p.clinical_status && <div><b>Status:</b> {p.clinical_status}</div>}
            {p.diagnosis && <div><b>Dx:</b> {p.diagnosis}</div>}
            {p.treatment && <div><b>Rx:</b> {p.treatment}</div>}
            {p.investigation_review && <div><b>Inv:</b> {p.investigation_review}</div>}
            {p.plan && <div><b>Plan:</b> {p.plan}</div>}
            {p.advice && <div><b>Advice:</b> {p.advice}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
